import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import {
    MessageCircle, BookOpen, Home, Menu, X, LogOut,
    User, Activity, ChevronDown, Zap, Settings, Shield,
    ArrowRight, Sparkles, Heart, ShieldCheck, Crown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Navbar = () => {
    const { t } = useTranslation();
    const { currentUser, userProfile, signOut } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    const isPro = userProfile?.subscriptionTier === 'pro' || userProfile?.subscriptionTier === 'premium' || userProfile?.consents?.proModeAccess === true;

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/auth');
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const navItems = [
        { name: t('nav.dashboard'), path: '/', icon: Home },
        { name: t('nav.chat'), path: '/chat', icon: MessageCircle },
        { name: t('nav.journal'), path: '/journal', icon: BookOpen },
    ];

    const initials = userProfile?.displayName
        ? userProfile.displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
        : currentUser?.displayName
            ? currentUser.displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
            : 'U';

    return (
        <>
            <nav className={`
                fixed top-0 left-0 right-0 z-50 transition-all duration-500 h-20 sm:h-24 flex items-center
                ${scrolled 
                    ? 'bg-white/70 backdrop-blur-2xl border-b border-slate-100/50 shadow-xl shadow-slate-200/20' 
                    : 'bg-transparent'}
            `}>
                <div className="max-w-7xl mx-auto px-6 w-full flex items-center justify-between">
                    
                    {/* ── LOGO ── */}
                    <div className="flex items-center gap-4 cursor-pointer group" onClick={() => navigate('/')}>
                        <div className="relative">
                            <motion.div 
                                whileHover={{ scale: 1.1 }}
                                className="relative w-12 h-12 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20 overflow-hidden border border-slate-100 bg-white"
                            >
                                <img src="/logo.png" alt="ManoSathi" className="w-full h-full object-cover" />
                            </motion.div>
                        </div>
                        <span className="font-black text-2xl tracking-tighter text-slate-900 group-hover:text-emerald-600 transition-colors">
                            {t('app_name')}
                        </span>
                    </div>

                    {/* ── DESKTOP NAVIGATION ── */}
                    <div className="hidden lg:flex items-center gap-2">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={(e) => {
                                        if ((item.path === '/chat' || item.path === '/journal') && !currentUser) {
                                            e.preventDefault();
                                            toast.info(t('common.login_required', { feature: item.name }));
                                            navigate('/auth');
                                        }
                                    }}
                                    className={`
                                        relative group flex items-center gap-2.5 px-5 py-2.5 rounded-2xl text-[13px] font-black uppercase tracking-widest transition-all duration-300
                                        ${isActive 
                                            ? 'text-slate-900' 
                                            : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'}
                                    `}
                                >
                                    <item.icon className={`w-4 h-4 transition-transform group-hover:scale-110 ${isActive ? 'text-emerald-500' : ''}`} />
                                    {item.name}
                                    {isActive && (
                                        <motion.div 
                                            layoutId="nav_active"
                                            className="absolute bottom-0 left-5 right-5 h-0.5 bg-emerald-500 rounded-full" 
                                        />
                                    )}
                                </NavLink>
                            );
                        })}

                        <div className="h-6 w-[1px] bg-slate-200 mx-4" />

                        <button
                            onClick={() => {
                                if (!currentUser) {
                                    toast.info(t('nav.pro_info'));
                                    navigate('/auth');
                                    return;
                                }
                                navigate('/pro-mode');
                            }}
                            className={`
                                flex items-center gap-2.5 px-6 py-2.5 rounded-2xl text-[13px] font-black uppercase tracking-widest shadow-xl transition-all hover:scale-105 active:scale-95
                                ${isPro 
                                    ? 'bg-slate-900 text-white shadow-slate-900/20' 
                                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-emerald-500/20'}
                            `}
                        >
                            {isPro ? <Crown className="w-4 h-4 text-yellow-400 fill-yellow-400" /> : <ShieldCheck className="w-4 h-4" />}
                            {isPro ? t('nav.pro_portal') : t('nav.pro')}
                        </button>
                    </div>

                    {/* ── USER MENU & SOS ── */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => toast.warning(t('panic.emergency_call'))}
                            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-rose-50 text-rose-500 border border-rose-100 hover:bg-rose-100 transition-all hover:scale-105"
                        >
                            <Zap className="w-3.5 h-3.5 fill-rose-500" />
                            {t('nav.sos')}
                        </button>

                        <div className="relative" ref={profileRef}>
                            {currentUser ? (
                                <>
                                    <button
                                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                                        className="flex items-center gap-3 p-1 rounded-2xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100"
                                    >
                                        <div className="relative h-10 w-10">
                                            <div className="h-full w-full rounded-2xl bg-slate-200 flex items-center justify-center text-slate-800 font-black border border-white shadow-sm overflow-hidden">
                                                {userProfile?.photoURL ? (
                                                    <img src={userProfile.photoURL} alt="User" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-sm">{initials}</span>
                                                )}
                                            </div>
                                            {isPro && (
                                                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-yellow-400 rounded-lg flex items-center justify-center shadow-lg transform rotate-12 border border-white">
                                                    <Crown className="w-3 h-3 text-white fill-white" />
                                                </div>
                                            )}
                                        </div>
                                        <ChevronDown className={`hidden md:block w-4 h-4 text-slate-400 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isProfileOpen && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute right-0 mt-4 w-72 bg-white/90 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white p-2 overflow-hidden origin-top-right"
                                            >
                                                <div className="px-5 py-6 bg-slate-50/50 rounded-2xl flex flex-col items-center text-center mb-2">
                                                    <div className="h-16 w-16 rounded-[1.5rem] bg-white shadow-xl mb-4 flex items-center justify-center text-xl font-black text-slate-800 border-2 border-slate-50 overflow-hidden">
                                                        {userProfile?.photoURL ? <img src={userProfile.photoURL} alt="User" className="w-full h-full object-cover" /> : <span>{initials}</span>}
                                                    </div>
                                                    <h3 className="text-base font-black text-slate-900 tracking-tight">{userProfile?.displayName || t('common.user')}</h3>
                                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{isPro ? t('modes.pro') : t('modes.standard')}</p>
                                                </div>
                                                
                                                <div className="space-y-1">
                                                    <button onClick={() => { navigate('/profile'); setIsProfileOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all">
                                                        <User className="w-4 h-4 text-emerald-500" /> {t('nav.profile')}
                                                    </button>
                                                    <button onClick={() => { navigate('/settings'); setIsProfileOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all">
                                                        <Settings className="w-4 h-4 text-blue-500" /> {t('nav.settings')}
                                                    </button>
                                                    {!isPro && (
                                                        <button onClick={() => { navigate('/pricing'); setIsProfileOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-all">
                                                            <Crown className="w-4 h-4 text-emerald-500" /> {t('nav.upgrade')}
                                                        </button>
                                                    )}
                                                </div>
                                                
                                                <div className="h-[1px] bg-slate-100 my-2" />
                                                
                                                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all">
                                                    <LogOut className="w-4 h-4" /> {t('nav.logout')}
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </>
                            ) : (
                                <button
                                    onClick={() => navigate('/auth')}
                                    className="px-6 py-2.5 rounded-2xl bg-slate-900 text-white font-black text-sm transition-all hover:bg-slate-800 hover:shadow-xl active:scale-95"
                                >
                                    {t('nav.get_started')}
                                </button>
                            )}
                        </div>

                        {/* Mobile Toggle */}
                        <div className="lg:hidden">
                            <button
                                className="p-2.5 rounded-2xl text-slate-800 hover:bg-slate-50 transition-all"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ── MOBILE MENU ── */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed inset-0 z-40 bg-white/95 backdrop-blur-2xl lg:hidden flex flex-col pt-32 px-6"
                    >
                        <div className="space-y-4">
                            {navItems.map((item) => (
                                <button
                                    key={item.path}
                                    onClick={() => { navigate(item.path); setIsMobileMenuOpen(false); }}
                                    className="w-full flex items-center gap-4 p-5 rounded-[2rem] bg-slate-50 border border-slate-100 text-slate-900 font-black text-lg group active:scale-95 transition-all"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <item.icon className="w-5 h-5 text-emerald-500" />
                                    </div>
                                    {item.name}
                                </button>
                            ))}
                            
                            <button
                                onClick={() => { navigate('/pro-mode'); setIsMobileMenuOpen(false); }}
                                className="w-full flex items-center gap-4 p-5 rounded-[2rem] bg-slate-900 text-white font-black text-lg shadow-xl shadow-slate-900/20 active:scale-95 transition-all"
                            >
                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                    <ShieldCheck className="w-5 h-5 text-white" />
                                </div>
                                {t('nav.pro_portal')}
                            </button>
                        </div>

                        <div className="mt-auto pb-12 flex flex-col items-center gap-6">
                            <div className="flex gap-4">
                                <div className="flex -space-x-3">
                                    {[1,2,3].map(i => <img key={i} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i+200}`} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="User" />)}
                                </div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t('nav.members')}</p>
                            </div>
                            <button onClick={handleLogout} className="text-sm font-black text-slate-400 hover:text-red-500 uppercase tracking-widest">{t('nav.logout')}</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
